/**
 * 邀请成为达人抽屉组件
 */
import { useState, useEffect } from "react";
import { getExpertInviteQrCode, type ExpertInviteQrCode } from "@/api/staff";

interface BecomeExpertDrawerProps {
  visible: boolean;
  onClose: () => void;
}

export default function BecomeExpertDrawer({ visible, onClose }: BecomeExpertDrawerProps) {
  const [qrData, setQrData] = useState<ExpertInviteQrCode | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (visible) {
      loadQrCode();
    }
  }, [visible]);

  const loadQrCode = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await getExpertInviteQrCode();
      setQrData(res);
    } catch (e: any) {
      console.error("[BecomeExpertDrawer] 获取二维码失败:", e);
      setError(e.message || "加载失败，请重试");
    } finally {
      setLoading(false);
    }
  };

  if (!visible) return null;

  return (
    <>
      {/* 遮罩层 */}
      <div className="fixed inset-0 bg-black/40 z-[100]" onClick={onClose} />

      {/* 抽屉内容 */}
      <div
        className="fixed bottom-0 left-0 right-0 z-[101] rounded-t-2xl bg-white animate-slide-up px-5 pt-4 pb-8"
        style={{ maxHeight: "90vh", overflowY: "auto", paddingBottom: "calc(2rem + env(safe-area-inset-bottom, 0px))" }}
      >
        {/* 顶部拖拽条 + 标题栏 */}
        <div className="flex flex-col items-center mb-5">
          <div className="w-10 h-1 rounded-full mb-3" style={{ background: "#DDDDDD" }} />
          <div className="flex items-center justify-between w-full relative">
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center cursor-pointer active:opacity-60"
            >
              <i className="ri-close-line text-xl" style={{ color: "#666666" }} />
            </button>
            <span
              className="text-[17px] font-semibold absolute left-1/2 -translate-x-1/2"
              style={{ color: "#333333" }}
            >
              邀请成为达人
            </span>
            <div className="w-8" />
          </div>
        </div>

        {/* 二维码区域 */}
        <div className="flex flex-col items-center">
          {loading ? (
            <div className="flex flex-col items-center py-12">
              <div className="w-12 h-12 border-2 border-gray-200 border-t-orange-500 rounded-full animate-spin mb-4" />
              <span className="text-sm text-gray-400">正在加载...</span>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center py-12">
              <i className="ri-error-warning-line text-5xl text-gray-300 mb-3" />
              <span className="text-sm text-gray-400 mb-4">{error}</span>
              <button
                onClick={loadQrCode}
                className="px-6 py-2 rounded-full text-sm text-white"
                style={{ background: "linear-gradient(135deg, #FF6B35, #FF9A5C)" }}
              >
                重新加载
              </button>
            </div>
          ) : qrData?.code_url ? (
            <div className="flex flex-col items-center">
              {/* 二维码图片 */}
              <div
                className="p-2 rounded-xl"
                style={{ background: "#FFFFFF", boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}
              >
                <img
                  src={qrData.code_url}
                  alt="邀请成为达人"
                  className="w-[140px] h-[140px] object-contain"
                />
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center py-12">
              <i className="ri-qr-code-line text-5xl text-gray-300 mb-3" />
              <span className="text-sm text-gray-400">暂无二维码</span>
            </div>
          )}

          {/* 提示文字 */}
          <p className="text-sm mt-6 text-center" style={{ color: "#666666" }}>
            扫描上方二维码，邀请好友成为达人
          </p>
        </div>
      </div>
    </>
  );
}
